import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  Paper
} from '@mui/material'
import * as actions from './Redux/actions'
import ChatTranscript from './Components/ChatTranscript/ChatTranscript'
import FloatingSimulator from './Components/FloatingSimulator/FloatingSimulator'
import FlowsList from './Components/FlowsList/FlowsList'
import SessionsList from './Components/SessionsList/SessionsList'
import { USSDSTART_FLOW_REF, LIVE_POLL_INTERVAL_MS } from './constants'

function TabPanel({ value, index, children }) {
  if (value !== index) return null
  return <Box sx={{ paddingTop: 2 }}>{children}</Box>
}

function UssdSessions(props) {
  const [msisdnInput, setMsisdnInput] = useState('')
  const [msisdn, setMsisdn] = useState('')
  const [tab, setTab] = useState(0)
  const [live, setLive] = useState(false)

  // Flows aren't scoped to a customer - browsable up front, with no phone number search needed.
  useEffect(() => {
    props.dispatch(actions.getUssdFlowsRequest())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const search = () => {
    const cleaned = msisdnInput.replace(/[^0-9]/g, '')
    setMsisdn(cleaned)
    props.dispatch(actions.getUssdSessionsRequest(cleaned))
    props.dispatch(actions.getUssdMessagesRequest(cleaned))
  }

  // Same reasoning as CustomerUssdSessionsPanel - opt-in polling once a customer is searched and Live is
  // switched on, so an actively progressing session shows up without needing to hit Search again.
  useEffect(() => {
    if (!msisdn || !live) return
    const interval = setInterval(() => {
      props.dispatch(actions.getUssdSessionsRequest(msisdn))
      props.dispatch(actions.getUssdMessagesRequest(msisdn))
    }, LIVE_POLL_INTERVAL_MS)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msisdn, live])

  return (
    <Paper sx={{ padding: 3 }}>
      <Typography variant="h5" gutterBottom>
        USSD Sessions
      </Typography>

      <Box
        sx={{ display: 'flex', gap: 2, alignItems: 'center', marginBottom: 2 }}
      >
        <TextField
          label="Customer phone number"
          placeholder="265991361790"
          value={msisdnInput}
          onChange={e => setMsisdnInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && search()}
          size="small"
        />
        <Button variant="contained" onClick={search}>
          Search
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label="Flows" />
          <Tab label="Sessions" />
          <Tab label="Chat" />
        </Tabs>
        {msisdn && (
          <FormControlLabel
            control={<Switch checked={live} onChange={e => setLive(e.target.checked)} size="small" />}
            label="Live"
          />
        )}
      </Box>

      <TabPanel value={tab} index={0}>
        <FlowsList
          flows={props.ussdSessions.ussd_flows}
          flowsLoading={props.ussdSessions.flows_loader}
        />
      </TabPanel>

      {msisdn ? (
        <>
          <TabPanel value={tab} index={1}>
            <SessionsList
              sessions={props.ussdSessions.ussd_sessions}
              sessionsLoading={props.ussdSessions.sessions_loader && !props.ussdSessions.ussd_sessions}
              messages={props.ussdSessions.ussd_messages}
              messagesLoading={props.ussdSessions.messages_loader && !props.ussdSessions.ussd_messages}
            />
          </TabPanel>

          <TabPanel value={tab} index={2}>
            <ChatTranscript
              messages={props.ussdSessions.ussd_messages}
              loading={props.ussdSessions.messages_loader && !props.ussdSessions.ussd_messages}
              sessions={props.ussdSessions.ussd_sessions}
            />
          </TabPanel>

          <FloatingSimulator flowUuid={USSDSTART_FLOW_REF} msisdn={msisdn} />
        </>
      ) : (
        [1, 2].includes(tab) && (
          <TabPanel value={tab} index={tab}>
            <Typography variant="body2">
              Search a customer phone number first.
            </Typography>
          </TabPanel>
        )
      )}
    </Paper>
  )
}

export default connect(state => ({
  ussdSessions: state.ussdSessions
}))(UssdSessions)
